import {
  formatQuantityDisplayValue,
  getIngredientCommonMeasures,
  getQuantityValueFromGrams,
} from "../recipes/recipeLogic";
import {
  getLocalizedCommonMeasureQuantityLabel,
  getInlineCommonMeasureLabel,
  normalizeCommonMeasureId,
} from "../shared/commonMeasures";
import i18n from "../../../Localization/i18next";
import BlackPng from "../../../assets/BlackPng.png";
import {
  createInitialPlanDraft,
  getLocalizedPlanMealNamePresets,
  getPlanMealsForSave,
} from "./nutritionPlanLogic";
import { normalizeNutritionWebsiteUrl } from "../../Firestore/nutrition/nutritionRecords";
import {
  getNutritionPlanDisplayNameFromSources,
} from "./nutritionPlanNameUtils";
import {
  buildNutritionPlanPdfDefinition,
  buildNutritionPlansPdfDefinition,
} from "./nutritionPlanPdfDefinition";

const PDF_COPY = {
  en: {
    chooseOne: "Choose one",
    notes: "Notes",
    generatedWith: "Generated with Efort",
    singlePlanTitle: "Nutrition plan",
    multiplePlansTitle: "Nutrition plans",
  },
  es: {
    chooseOne: "Elige una opcion",
    notes: "Notas",
    generatedWith: "Generado con Efort",
    singlePlanTitle: "Plan nutricional",
    multiplePlansTitle: "Planes nutricionales",
  },
};

let pdfMakeLoaderPromise = null;
const PDF_SAFE_IMAGE_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/jpg"]);
export const NUTRITION_PDF_ICON_FONT_FAMILY = "NutritionPdfIcons";
const NUTRITION_PDF_ICON_FONT_FILE_NAME = "NutritionPdfIcons-Ionicons.ttf";
export const NUTRITION_PDF_HEADER_LOGO_BOX = {
  width: 84,
  height: 28,
};
const NUTRITION_PDF_HEADER_LOGO_EXPORT_SCALE = 3;
const PDF_SOFT_WRAP_INTERVAL = 12;
const PDF_PAGE_CONTENT_MAX_HEIGHT = 620;
const PDF_MEAL_HEADER_HEIGHT = 47;
const PDF_MEAL_NOTES_SECTION_HEIGHT = 26;
const PDF_MEAL_CARD_BOTTOM_PADDING = 14;
const PDF_MEAL_CONTENT_WIDTH = 527;
const PDF_MEAL_OPTION_GRID_COLUMN_GAP = 18;
const PDF_MEAL_OPTION_COLUMN_WIDTH =
  (PDF_MEAL_CONTENT_WIDTH - PDF_MEAL_OPTION_GRID_COLUMN_GAP) / 2;
const PDF_BULLET_COLUMN_WIDTH = 10;
const PDF_OPTION_BLOCK_VERTICAL_SPACING = 4;
const PDF_CHOICE_OPTION_BOTTOM_SPACING = 1;
const PDF_CHOICE_GROUP_HORIZONTAL_PADDING = 10;
const PDF_OPTION_TITLE_FONT_SIZE = 8;
const PDF_OPTION_TITLE_LINE_HEIGHT = 1.35;
const PDF_ROW_TEXT_FONT_SIZE = 10;
const PDF_ROW_TEXT_LINE_HEIGHT = 1.35;
const PDF_NOTES_TEXT_FONT_SIZE = 10;
const PDF_NOTES_TEXT_LINE_HEIGHT = 1.4;
const PDF_MEAL_TITLE_FONT_SIZE = 11;
const PDF_MEAL_TITLE_LINE_HEIGHT = 1;
const PDF_INLINE_QUANTITY_FONT_SIZE = 9;
const PDF_AVERAGE_GLYPH_WIDTH_FACTOR = 0.52;

function isSpanishLanguage(language = "en") {
  return String(language ?? "").toLowerCase().startsWith("es");
}

function getPdfCopy(language = "en") {
  return isSpanishLanguage(language) ? PDF_COPY.es : PDF_COPY.en;
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveLocalizedTextValue(value, language = "en") {
  if (typeof value === "string") {
    return value.trim();
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  const primaryKey = isSpanishLanguage(language) ? "es" : "en";
  const secondaryKey = primaryKey === "es" ? "en" : "es";
  const primaryValue = normalizeText(value?.[primaryKey]);
  if (primaryValue) {
    return primaryValue;
  }

  const secondaryValue = normalizeText(value?.[secondaryKey]);
  if (secondaryValue) {
    return secondaryValue;
  }

  return Object.values(value).map(normalizeText).find(Boolean) ?? "";
}

function getCommonMeasureTranslator(language = "en") {
  const normalizedLanguage = isSpanishLanguage(language) ? "es" : "en";
  return i18n.getFixedT(normalizedLanguage);
}

function getCoachDisplayName(coach) {
  return [coach?.name, coach?.surname]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ")
    .trim();
}

function getMeasureLabel(unitKey, quantityValue, language = "en") {
  const resolvedUnitKey = String(unitKey ?? "").trim();
  const normalizedUnitKey =
    resolvedUnitKey === "grams"
      ? "grams"
      : normalizeCommonMeasureId(resolvedUnitKey, resolvedUnitKey);
  if (!normalizedUnitKey || normalizedUnitKey === "grams") {
    return "g";
  }

  return getLocalizedCommonMeasureQuantityLabel(normalizedUnitKey, {
    t: getCommonMeasureTranslator(language),
    count: quantityValue,
  });
}

function formatFoodQuantity(item, language = "en") {
  const commonMeasures = getIngredientCommonMeasures(item);
  const unitKey =
    String(
      item?.selected_measure_id ??
        item?.selectedMeasureId ??
        item?.quantityUnitKey ??
        "grams",
    ).trim() || "grams";
  const quantityInGrams = Number(item?.quantity_g ?? item?.reference_quantity_g ?? 0);

  if (!Number.isFinite(quantityInGrams) || quantityInGrams < 0) {
    return "";
  }

  const formattedGrams = formatQuantityDisplayValue(quantityInGrams, {
    zeroFallback: true,
  });

  if (unitKey === "grams") {
    return formattedGrams ? `${formattedGrams} g` : "";
  }

  const quantityValue =
    quantityInGrams === 0
      ? "0"
      : getQuantityValueFromGrams(quantityInGrams, unitKey, commonMeasures);
  const measureLabel = getInlineCommonMeasureLabel(
    getMeasureLabel(unitKey, quantityValue, language),
  );
  if (!quantityValue) {
    return formattedGrams ? `${formattedGrams} g` : "";
  }

  return formattedGrams
    ? `${quantityValue} ${measureLabel} (${formattedGrams} g)`
    : `${quantityValue} ${measureLabel}`;
}

function buildFoodRow(item = {}, language = "en") {
  const normalizedLink = normalizeNutritionWebsiteUrl(item?.website);

  return {
    kind: "food",
    name: normalizeText(item?.name),
    quantityLabel: formatFoodQuantity(item, language),
    ...(normalizedLink ? { link: normalizedLink } : {}),
  };
}

function buildChoiceGroup(choiceGroup = {}, language = "en") {
  const options = (Array.isArray(choiceGroup?.options) ? choiceGroup.options : [])
    .map((option) => buildFoodRow(option, language))
    .filter((option) => option.name);

  if (options.length === 0) {
    return null;
  }

  return {
    kind: "choice_group",
    title: getPdfCopy(language).chooseOne,
    options,
  };
}

function buildItemBlocks(optionItems = [], language = "en") {
  const blocks = [];

  for (let index = 0; index < optionItems.length; index += 1) {
    const currentItem = optionItems[index];
    if (!currentItem || typeof currentItem !== "object") {
      continue;
    }

    if (currentItem.type === "equivalence") {
      const standaloneChoiceGroup = buildChoiceGroup(currentItem, language);
      if (standaloneChoiceGroup) {
        blocks.push(standaloneChoiceGroup);
      }
      continue;
    }

    const foodRow = buildFoodRow(currentItem, language);
    if (foodRow.name) {
      blocks.push(foodRow);
    }
  }

  return blocks;
}

function getExportMealName(meal = {}, mealIndex = 0, language = "en") {
  const mealName = normalizeText(meal?.name);
  const presetMealNames = getLocalizedPlanMealNamePresets(language);

  if (!mealName) {
    return presetMealNames[mealIndex] ?? "";
  }

  const genericMealMatch = /^meal\s+(\d+)$/i.exec(mealName);
  if (genericMealMatch && Number(genericMealMatch[1]) === mealIndex + 1) {
    return presetMealNames[mealIndex] ?? mealName;
  }

  return mealName;
}

function getEstimatedTextLineCount(
  text,
  {
    availableWidth = 0,
    fontSize = PDF_ROW_TEXT_FONT_SIZE,
  } = {},
) {
  const normalizedText = normalizeText(text);

  if (!normalizedText) {
    return 0;
  }

  const safeAvailableWidth = Number(availableWidth);
  if (!Number.isFinite(safeAvailableWidth) || safeAvailableWidth <= 0) {
    return 1;
  }

  const averageGlyphWidth = Math.max(1, fontSize * PDF_AVERAGE_GLYPH_WIDTH_FACTOR);
  const maxCharsPerLine = Math.max(1, Math.floor(safeAvailableWidth / averageGlyphWidth));
  const preferredChunkSize = Math.max(1, Math.min(maxCharsPerLine, PDF_SOFT_WRAP_INTERVAL));
  const words = normalizedText.split(/\s+/).filter(Boolean);
  let lineCount = 1;
  let currentLineLength = 0;

  words.forEach((word) => {
    const segments = [];

    for (let index = 0; index < word.length; index += preferredChunkSize) {
      segments.push(word.slice(index, index + preferredChunkSize));
    }

    segments.forEach((segment, segmentIndex) => {
      const requiredLength =
        currentLineLength > 0 && segmentIndex === 0
          ? currentLineLength + 1 + segment.length
          : currentLineLength + segment.length;

      if (requiredLength <= maxCharsPerLine) {
        currentLineLength =
          currentLineLength > 0 && segmentIndex === 0
            ? currentLineLength + 1 + segment.length
            : currentLineLength + segment.length;
        return;
      }

      if (currentLineLength > 0) {
        lineCount += 1;
      }

      currentLineLength = segment.length;
    });
  });

  return Math.max(1, lineCount);
}

function getEstimatedTextHeight(text, { availableWidth = 0, fontSize, lineHeight } = {}) {
  const lineCount = getEstimatedTextLineCount(text, {
    availableWidth,
    fontSize,
  });

  return lineCount * fontSize * lineHeight;
}

function estimateFoodRowHeight(block, {
  nameWidth = 0,
  quantityWidth = 0,
  bottomSpacing = PDF_OPTION_BLOCK_VERTICAL_SPACING,
} = {}) {
  const quantityHeight = block?.quantityLabel
    ? getEstimatedTextHeight(block.quantityLabel, {
        availableWidth: quantityWidth,
        fontSize: PDF_INLINE_QUANTITY_FONT_SIZE,
        lineHeight: PDF_ROW_TEXT_LINE_HEIGHT,
      })
    : 0;
  const nameHeight = getEstimatedTextHeight(block?.name, {
    availableWidth: nameWidth,
    fontSize: PDF_ROW_TEXT_FONT_SIZE,
    lineHeight: PDF_ROW_TEXT_LINE_HEIGHT,
  });

  return Math.max(quantityHeight, nameHeight, PDF_ROW_TEXT_FONT_SIZE * PDF_ROW_TEXT_LINE_HEIGHT) +
    bottomSpacing;
}

function estimateChoiceGroupHeight(block, optionWidth) {
  const innerWidth = Math.max(0, optionWidth - PDF_CHOICE_GROUP_HORIZONTAL_PADDING * 2);
  const titleHeight = estimateFoodRowHeight(
    {
      quantityLabel: "",
      name: block?.title,
    },
    {
      nameWidth: Math.max(0, innerWidth - PDF_BULLET_COLUMN_WIDTH),
      quantityWidth: 0,
      bottomSpacing: PDF_OPTION_BLOCK_VERTICAL_SPACING,
    },
  );

  const optionHeights = (Array.isArray(block?.options) ? block.options : []).reduce(
    (total, option) =>
      total +
      estimateFoodRowHeight(option, {
        nameWidth: Math.max(0, innerWidth - 16 - PDF_BULLET_COLUMN_WIDTH - 44),
        quantityWidth: 38,
        bottomSpacing: PDF_CHOICE_OPTION_BOTTOM_SPACING,
      }),
    0,
  );

  return 12 + titleHeight + optionHeights;
}

function estimateOptionHeight(option, optionWidth) {
  const normalizedOptionWidth = Math.max(0, optionWidth);
  const titleHeight = option?.showTitle
    ? 2 +
      getEstimatedTextHeight(option?.name, {
        availableWidth: normalizedOptionWidth,
        fontSize: PDF_OPTION_TITLE_FONT_SIZE,
        lineHeight: PDF_OPTION_TITLE_LINE_HEIGHT,
      }) +
      4
    : 0;
  const blockHeights = (Array.isArray(option?.blocks) ? option.blocks : []).reduce(
    (total, block) => {
      if (block?.kind === "choice_group") {
        return total + estimateChoiceGroupHeight(block, normalizedOptionWidth);
      }

      return total +
        estimateFoodRowHeight(block, {
          nameWidth: Math.max(0, normalizedOptionWidth - PDF_BULLET_COLUMN_WIDTH - 44),
          quantityWidth: 38,
          bottomSpacing: PDF_OPTION_BLOCK_VERTICAL_SPACING,
        });
    },
    0,
  );

  return titleHeight + blockHeights;
}

function estimateMealRenderedHeight(meal) {
  const optionHeights = (Array.isArray(meal?.options) ? meal.options : []).map((option) =>
    estimateOptionHeight(
      option,
      (Array.isArray(meal?.options) ? meal.options.length : 0) > 1
        ? PDF_MEAL_OPTION_COLUMN_WIDTH
        : PDF_MEAL_CONTENT_WIDTH,
    ),
  );
  const pairedOptionHeight =
    optionHeights.length <= 1
      ? (optionHeights[0] ?? 0)
      : optionHeights.reduce((rows, height, index) => {
          if (index % 2 === 0) {
            rows.push(height);
            return rows;
          }

          rows[rows.length - 1] = Math.max(rows[rows.length - 1], height);
          return rows;
        }, []).reduce((total, rowHeight) => total + rowHeight, 0);
  const notesHeight = meal?.notes
    ? PDF_MEAL_NOTES_SECTION_HEIGHT +
      getEstimatedTextHeight(meal.notes, {
        availableWidth: PDF_MEAL_CONTENT_WIDTH,
        fontSize: PDF_NOTES_TEXT_FONT_SIZE,
        lineHeight: PDF_NOTES_TEXT_LINE_HEIGHT,
      })
    : 0;
  const headerHeight =
    PDF_MEAL_HEADER_HEIGHT +
    getEstimatedTextHeight(meal?.name, {
      availableWidth: PDF_MEAL_CONTENT_WIDTH - 60,
      fontSize: PDF_MEAL_TITLE_FONT_SIZE,
      lineHeight: PDF_MEAL_TITLE_LINE_HEIGHT,
    });

  return headerHeight + pairedOptionHeight + notesHeight + PDF_MEAL_CARD_BOTTOM_PADDING;
}

function canSafelyKeepMealTogether(meal) {
  return estimateMealRenderedHeight(meal) <= PDF_PAGE_CONTENT_MAX_HEIGHT;
}

function resolveNutritionPlanPdfMeals(planLike = {}) {
  const directSavedMeals = getPlanMealsForSave(planLike);
  const rawMeals = Array.isArray(planLike?.meals) ? planLike.meals : [];
  const inflatedFallbackMeals =
    rawMeals.length > 0 ? getPlanMealsForSave(createInitialPlanDraft(planLike)) : [];

  const getMealStructureScore = (meals = []) =>
    (Array.isArray(meals) ? meals : []).reduce((score, meal) => {
      const options = Array.isArray(meal?.options) ? meal.options : [];
      const itemCount = options.reduce(
        (total, option) => total + (Array.isArray(option?.items) ? option.items.length : 0),
        0,
      );
      const hasRenderableContent =
        itemCount > 0 || normalizeText(meal?.notes).length > 0;

      return hasRenderableContent ? score + 10000 + options.length * 100 + itemCount : score;
    }, 0);

  const candidateMeals = [directSavedMeals, rawMeals, inflatedFallbackMeals];
  const resolvedMeals = candidateMeals.reduce((bestMeals, nextMeals) => {
    return getMealStructureScore(nextMeals) > getMealStructureScore(bestMeals)
      ? nextMeals
      : bestMeals;
  }, directSavedMeals);

  if (
    resolvedMeals.length > 0 ||
    !Array.isArray(planLike?.meals) ||
    planLike.meals.length === 0
  ) {
    return resolvedMeals;
  }

  return getPlanMealsForSave(createInitialPlanDraft(planLike));
}

function sanitizePdfFileName(value, fallback = "nutrition-plan") {
  const sanitizedValue = String(value ?? "")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sanitizedValue || fallback;
}

function resolveSinglePlanPdfFileBase(planLike = {}, language = "en") {
  const copy = getPdfCopy(language);
  const sanitizedPlanName = sanitizePdfFileName(
    resolveLocalizedTextValue(planLike?.name, language),
    "",
  );

  return sanitizedPlanName || copy.singlePlanTitle;
}

function formatNutritionPlanPdfFileDate(value, language = "en") {
  const parsedDate = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) {
    return "";
  }

  const dateParts = new Intl.DateTimeFormat(
    isSpanishLanguage(language) ? "es-ES" : "en-US",
    {
      day: "numeric",
      month: "short",
    },
  ).formatToParts(parsedDate);
  const day = dateParts.find((part) => part.type === "day")?.value ?? "";
  const month = dateParts.find((part) => part.type === "month")?.value ?? "";

  return [day, month].filter(Boolean).join(" ");
}

function buildNutritionPdfFileName({
  baseLabel = "",
  athleteName = "",
  exportedAt = new Date(),
  language = "en",
  fallbackBase = "nutrition-plan",
} = {}) {
  const sanitizedBaseLabel = sanitizePdfFileName(baseLabel, fallbackBase);
  const sanitizedAthleteName = sanitizePdfFileName(athleteName, "");
  const formattedDate = sanitizePdfFileName(
    formatNutritionPlanPdfFileDate(exportedAt, language),
    "",
  );
  let fileBase = sanitizedBaseLabel;

  if (sanitizedAthleteName) {
    fileBase = `${fileBase} - ${sanitizedAthleteName}`;
  }

  if (formattedDate) {
    fileBase = `${fileBase} (${formattedDate})`;
  }

  return `${sanitizePdfFileName(fileBase, sanitizedBaseLabel)}.pdf`;
}

function formatNutritionPlanPdfExportDate(value, language = "en") {
  const parsedDate = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) {
    return "";
  }

  const dateParts = new Intl.DateTimeFormat(
    isSpanishLanguage(language) ? "es-ES" : "en-US",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  ).formatToParts(parsedDate);
  const day = dateParts.find((part) => part.type === "day")?.value ?? "";
  const month = dateParts.find((part) => part.type === "month")?.value ?? "";
  const year = dateParts.find((part) => part.type === "year")?.value ?? "";

  return [day, month, year].filter(Boolean).join(" ");
}

export function buildNutritionPlanPdfFileName({
  planLike = {},
  athleteName = "",
  language = "en",
  exportedAt = new Date(),
} = {}) {
  return buildNutritionPdfFileName({
    baseLabel: resolveSinglePlanPdfFileBase(planLike, language),
    athleteName,
    exportedAt,
    language,
    fallbackBase: getPdfCopy(language).singlePlanTitle,
  });
}

export function buildNutritionPlansPdfFileName({
  athleteName = "",
  language = "en",
  exportedAt = new Date(),
} = {}) {
  const copy = getPdfCopy(language);

  return buildNutritionPdfFileName({
    baseLabel: copy.multiplePlansTitle,
    athleteName,
    exportedAt,
    language,
    fallbackBase: copy.multiplePlansTitle,
  });
}

export function buildNutritionPlanPdfSnapshot({
  planEntity = null,
  draft = null,
  rootRecord = null,
  coach = null,
  athleteName = "",
  language = "en",
  exportedAt = new Date(),
} = {}) {
  const resolvedDraft =
    draft ?? planEntity?.getDraftSnapshot?.() ?? planEntity?.draft ?? null;

  if (!resolvedDraft || typeof resolvedDraft !== "object") {
    return null;
  }

  const pdfMeals = resolveNutritionPlanPdfMeals(resolvedDraft);
  const meals = pdfMeals
    .map((meal, mealIndex) => {
      const renderedOptions = (Array.isArray(meal?.options) ? meal.options : [])
        .map((option) => {
          const blocks = buildItemBlocks(option?.items, language);
          if (blocks.length === 0) {
            return null;
          }

          return {
            id: option?.id ?? null,
            name: normalizeText(option?.name),
            showTitle: (Array.isArray(meal?.options) ? meal.options.length : 0) > 1,
            blocks,
          };
        })
        .filter(Boolean);

      const normalizedNotes = normalizeText(meal?.notes);

      return {
        id: meal?.id ?? null,
        name: getExportMealName(meal, mealIndex, language),
        notes: normalizedNotes,
        options: renderedOptions,
        keepTogether: canSafelyKeepMealTogether({
          ...meal,
          notes: normalizedNotes,
          options: renderedOptions,
        }),
      };
    })
    .filter((meal) => meal.options.length > 0 || meal.notes.length > 0);

  const copy = getPdfCopy(language);
  const title = getNutritionPlanDisplayNameFromSources(
    [
      resolvedDraft,
      planEntity?.getDraftSnapshot?.(),
      planEntity,
      rootRecord,
    ],
    language,
  );

  return {
    language,
    fileName: buildNutritionPlanPdfFileName({
      planLike: resolvedDraft,
      athleteName,
      language,
      exportedAt,
    }),
    title,
    pdfMetadataSinglePlanTitle: copy.singlePlanTitle,
    pdfMetadataMultiplePlansTitle: copy.multiplePlansTitle,
    athleteName: normalizeText(athleteName),
    exportDateLabel: formatNutritionPlanPdfExportDate(exportedAt, language),
    generatedWithLabel: copy.generatedWith,
    notesLabel: copy.notes,
    branding: {
      coachName: getCoachDisplayName(coach),
      coachLogoUrl: normalizeText(coach?.logoUrl) || null,
    },
    plan: {
      title,
      notes: normalizeText(resolvedDraft?.notes),
      meals,
    },
  };
}

function resolvePlanPdfSourceDisplayName(planSource = {}, language = "en") {
  return getNutritionPlanDisplayNameFromSources(
    [
      planSource?.draft,
      planSource?.planEntity?.getDraftSnapshot?.(),
      planSource?.planEntity,
      planSource?.rootRecord,
      planSource,
    ],
    language,
  );
}

export function buildNutritionPlanPdfSnapshotsFromSources({
  planSources = [],
  coach = null,
  athleteName = "",
  language = "en",
  exportedAt = new Date(),
} = {}) {
  return (Array.isArray(planSources) ? planSources : []).reduce(
    (accumulator, planSource) => {
      const snapshot = buildNutritionPlanPdfSnapshot({
        planEntity: planSource?.planEntity ?? null,
        draft: planSource?.draft ?? null,
        rootRecord: planSource?.rootRecord ?? null,
        coach,
        athleteName,
        language,
        exportedAt,
      });

      if (snapshot) {
        accumulator.snapshots.push(snapshot);
      } else {
        accumulator.skippedPlanNames.push(
          resolvePlanPdfSourceDisplayName(planSource, language),
        );
      }

      return accumulator;
    },
    { snapshots: [], skippedPlanNames: [] },
  );
}

async function blobToDataUrl(blob) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read image blob"));
    reader.onloadend = () =>
      resolve(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(blob);
  });
}

function arrayBufferToDataUrl(arrayBuffer, mimeType = "application/octet-stream") {
  if (!arrayBuffer) {
    return null;
  }

  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  const encodedValue =
    typeof btoa === "function"
      ? btoa(binary)
      : typeof Buffer !== "undefined"
        ? Buffer.from(binary, "binary").toString("base64")
        : null;

  return encodedValue ? `data:${mimeType};base64,${encodedValue}` : null;
}

function arrayBufferToBase64(arrayBuffer) {
  if (!arrayBuffer) {
    return null;
  }

  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return typeof btoa === "function"
    ? btoa(binary)
    : typeof Buffer !== "undefined"
      ? Buffer.from(binary, "binary").toString("base64")
      : null;
}

function normalizeMimeType(value) {
  return String(value ?? "")
    .split(";")[0]
    .trim()
    .toLowerCase();
}

function isImageMimeType(value) {
  return normalizeMimeType(value).startsWith("image/");
}

function isPdfSafeImageMimeType(value) {
  return PDF_SAFE_IMAGE_MIME_TYPES.has(normalizeMimeType(value));
}

function parseDataUrlMimeType(value) {
  const match = String(value ?? "").match(/^data:([^;,]+)[;,]/i);
  return normalizeMimeType(match?.[1]);
}

function inferImageMimeTypeFromUrl(value) {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return "";
  }

  const lowerValue = normalizedValue.toLowerCase().split("?")[0].split("#")[0];
  if (lowerValue.endsWith(".png")) {
    return "image/png";
  }
  if (lowerValue.endsWith(".jpg") || lowerValue.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lowerValue.endsWith(".webp")) {
    return "image/webp";
  }
  if (lowerValue.endsWith(".svg")) {
    return "image/svg+xml";
  }

  return "";
}

function resolveFetchedImageMimeType(response, blob, sourceUrl) {
  const candidateMimeTypes = [
    blob?.type,
    response?.headers?.get?.("content-type"),
    inferImageMimeTypeFromUrl(sourceUrl),
  ];

  for (const candidate of candidateMimeTypes) {
    const normalizedCandidate = normalizeMimeType(candidate);
    if (normalizedCandidate) {
      return normalizedCandidate;
    }
  }

  return "";
}

function getImagePixel(data, width, x, y) {
  const index = (y * width + x) * 4;
  return {
    r: data[index] ?? 0,
    g: data[index + 1] ?? 0,
    b: data[index + 2] ?? 0,
    a: data[index + 3] ?? 0,
  };
}

function averagePixelColor(pixels = []) {
  if (!Array.isArray(pixels) || pixels.length === 0) {
    return null;
  }

  const totals = pixels.reduce(
    (accumulator, pixel) => ({
      r: accumulator.r + (pixel?.r ?? 0),
      g: accumulator.g + (pixel?.g ?? 0),
      b: accumulator.b + (pixel?.b ?? 0),
      a: accumulator.a + (pixel?.a ?? 0),
    }),
    { r: 0, g: 0, b: 0, a: 0 },
  );

  return {
    r: Math.round(totals.r / pixels.length),
    g: Math.round(totals.g / pixels.length),
    b: Math.round(totals.b / pixels.length),
    a: Math.round(totals.a / pixels.length),
  };
}

function getMaxChannelDelta(leftPixel, rightPixel) {
  return Math.max(
    Math.abs((leftPixel?.r ?? 0) - (rightPixel?.r ?? 0)),
    Math.abs((leftPixel?.g ?? 0) - (rightPixel?.g ?? 0)),
    Math.abs((leftPixel?.b ?? 0) - (rightPixel?.b ?? 0)),
    Math.abs((leftPixel?.a ?? 0) - (rightPixel?.a ?? 0)),
  );
}

function isPixelNearColor(pixel, referenceColor, tolerance = 12) {
  return getMaxChannelDelta(pixel, referenceColor) <= tolerance;
}

function detectUniformEdgeMatte(
  imageData,
  width,
  height,
  {
    minOpaqueAlpha = 245,
    colorTolerance = 12,
    edgeCoverageThreshold = 0.9,
  } = {},
) {
  if (!imageData || width <= 0 || height <= 0) {
    return null;
  }

  const cornerPixels = [
    getImagePixel(imageData, width, 0, 0),
    getImagePixel(imageData, width, width - 1, 0),
    getImagePixel(imageData, width, 0, height - 1),
    getImagePixel(imageData, width, width - 1, height - 1),
  ];

  if (cornerPixels.some((pixel) => pixel.a < minOpaqueAlpha)) {
    return null;
  }

  const referenceColor = averagePixelColor(cornerPixels);
  if (
    cornerPixels.some(
      (pixel) => !isPixelNearColor(pixel, referenceColor, colorTolerance),
    )
  ) {
    return null;
  }

  const edgeThickness = Math.max(1, Math.floor(Math.min(width, height) * 0.08));
  let edgePixelCount = 0;
  let matchingEdgePixelCount = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const isEdgePixel =
        x < edgeThickness ||
        y < edgeThickness ||
        x >= width - edgeThickness ||
        y >= height - edgeThickness;

      if (!isEdgePixel) {
        continue;
      }

      edgePixelCount += 1;
      if (isPixelNearColor(getImagePixel(imageData, width, x, y), referenceColor, colorTolerance)) {
        matchingEdgePixelCount += 1;
      }
    }
  }

  if (
    edgePixelCount === 0 ||
    matchingEdgePixelCount / edgePixelCount < edgeCoverageThreshold
  ) {
    return null;
  }

  return referenceColor;
}

function resolveVisibleImageContentBounds(
  imageData,
  width,
  height,
  {
    alphaThreshold = 12,
    matteColor = null,
    matteColorTolerance = 12,
    matteAlphaThreshold = 245,
  } = {},
) {
  if (!imageData || width <= 0 || height <= 0) {
    return null;
  }

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixel = getImagePixel(imageData, width, x, y);
      if (pixel.a <= alphaThreshold) {
        continue;
      }

      if (
        matteColor &&
        pixel.a >= matteAlphaThreshold &&
        isPixelNearColor(pixel, matteColor, matteColorTolerance)
      ) {
        continue;
      }

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

async function convertImageUrlToPngDataUrl(
  imageUrl,
  {
    documentRef = typeof document !== "undefined" ? document : null,
    urlRef = typeof URL !== "undefined" ? URL : null,
    ImageCtor = typeof Image !== "undefined" ? Image : null,
    targetWidth = 0,
    targetHeight = 0,
    mode = "contain",
    borderRadius = 0,
    backgroundColor = null,
    exportScale = 1,
    renderWidthForBorderRadius = 0,
    renderHeightForBorderRadius = 0,
    trimTransparentPadding = false,
    trimUniformEdgeMatte = false,
  } = {},
) {
  if (!imageUrl || !documentRef?.createElement || !ImageCtor) {
    return null;
  }

  return await new Promise((resolve) => {
    const image = new ImageCtor();
    let objectUrlToRevoke = null;

    const cleanup = () => {
      if (objectUrlToRevoke && typeof urlRef?.revokeObjectURL === "function") {
        urlRef.revokeObjectURL(objectUrlToRevoke);
      }
    };

    image.onload = () => {
      try {
        const canvas = documentRef.createElement("canvas");
        const sourceWidth = image.naturalWidth || image.width || 0;
        const sourceHeight = image.naturalHeight || image.height || 0;
        const width = targetWidth || sourceWidth;
        const height = targetHeight || sourceHeight;

        if (!canvas || width <= 0 || height <= 0 || sourceWidth <= 0 || sourceHeight <= 0) {
          cleanup();
          resolve(null);
          return;
        }

        const resolvedExportScale =
          targetWidth > 0 || targetHeight > 0
            ? Math.max(1, Number(exportScale) || 1)
            : 1;

        canvas.width = Math.round(width * resolvedExportScale);
        canvas.height = Math.round(height * resolvedExportScale);
        const context = canvas.getContext?.("2d");
        if (!context) {
          cleanup();
          resolve(null);
          return;
        }

        if (resolvedExportScale !== 1 && typeof context.scale === "function") {
          context.scale(resolvedExportScale, resolvedExportScale);
        }

        if ("imageSmoothingEnabled" in context) {
          context.imageSmoothingEnabled = true;
        }

        if ("imageSmoothingQuality" in context) {
          context.imageSmoothingQuality = "high";
        }

        let sourceCropX = 0;
        let sourceCropY = 0;
        let sourceCropWidth = sourceWidth;
        let sourceCropHeight = sourceHeight;

        if (trimTransparentPadding || trimUniformEdgeMatte) {
          const analysisCanvas = documentRef.createElement("canvas");
          const analysisContext = analysisCanvas?.getContext?.("2d");

          if (analysisCanvas && analysisContext) {
            analysisCanvas.width = sourceWidth;
            analysisCanvas.height = sourceHeight;
            analysisContext.drawImage(image, 0, 0, sourceWidth, sourceHeight);

            const imageData = analysisContext.getImageData?.(
              0,
              0,
              sourceWidth,
              sourceHeight,
            )?.data;
            const matteColor =
              trimUniformEdgeMatte && imageData
                ? detectUniformEdgeMatte(imageData, sourceWidth, sourceHeight)
                : null;
            const visibleBounds = imageData
              ? resolveVisibleImageContentBounds(imageData, sourceWidth, sourceHeight, {
                  matteColor,
                })
              : null;

            if (visibleBounds) {
              sourceCropX = visibleBounds.x;
              sourceCropY = visibleBounds.y;
              sourceCropWidth = visibleBounds.width;
              sourceCropHeight = visibleBounds.height;
            }
          }
        }

        const resolvedMode = mode === "cover" ? "cover" : "contain";
        const scale =
          targetWidth > 0 || targetHeight > 0
            ? resolvedMode === "cover"
              ? Math.max(width / sourceCropWidth, height / sourceCropHeight)
              : Math.min(width / sourceCropWidth, height / sourceCropHeight)
            : 1;
        const drawWidth = sourceCropWidth * scale;
        const drawHeight = sourceCropHeight * scale;
        const offsetX = (width - drawWidth) / 2;
        const offsetY = (height - drawHeight) / 2;

        if (backgroundColor) {
          context.fillStyle = backgroundColor;
          context.fillRect(0, 0, width, height);
        }

        if (borderRadius > 0) {
          const borderRadiusScale =
            targetWidth > 0 || targetHeight > 0
              ? 1
              : renderWidthForBorderRadius > 0 && renderHeightForBorderRadius > 0
                ? Math.min(
                    renderWidthForBorderRadius / sourceWidth,
                    renderHeightForBorderRadius / sourceHeight,
                  ) || 1
                : 1;
          const resolvedBorderRadius =
            borderRadiusScale > 0 ? borderRadius / borderRadiusScale : borderRadius;
          const safeRadius = Math.min(resolvedBorderRadius, width / 2, height / 2);
          context.save();
          context.beginPath();
          context.moveTo(safeRadius, 0);
          context.lineTo(width - safeRadius, 0);
          context.quadraticCurveTo(width, 0, width, safeRadius);
          context.lineTo(width, height - safeRadius);
          context.quadraticCurveTo(width, height, width - safeRadius, height);
          context.lineTo(safeRadius, height);
          context.quadraticCurveTo(0, height, 0, height - safeRadius);
          context.lineTo(0, safeRadius);
          context.quadraticCurveTo(0, 0, safeRadius, 0);
          context.closePath();
          context.clip();
        }

        context.drawImage(
          image,
          sourceCropX,
          sourceCropY,
          sourceCropWidth,
          sourceCropHeight,
          offsetX,
          offsetY,
          drawWidth,
          drawHeight,
        );

        if (borderRadius > 0) {
          context.restore();
        }

        const pngDataUrl = canvas.toDataURL("image/png");
        cleanup();
        resolve(typeof pngDataUrl === "string" ? pngDataUrl : null);
      } catch {
        cleanup();
        resolve(null);
      }
    };

    image.onerror = () => {
      cleanup();
      resolve(null);
    };

    image.src = imageUrl;
  });
}

async function convertBlobToPngDataUrl(
  blob,
  {
    urlRef = typeof URL !== "undefined" ? URL : null,
    convertImageUrlToPngDataUrlImpl = convertImageUrlToPngDataUrl,
  } = {},
) {
  if (!blob || typeof urlRef?.createObjectURL !== "function") {
    return null;
  }

  const objectUrl = urlRef.createObjectURL(blob);
  return await convertImageUrlToPngDataUrlImpl(objectUrl, { urlRef });
}

export async function formatNutritionPlanPdfHeaderLogoDataUrl(
  imageDataUrl,
  {
    convertImageUrlToPngDataUrlImpl = convertImageUrlToPngDataUrl,
    width = NUTRITION_PDF_HEADER_LOGO_BOX.width,
    height = NUTRITION_PDF_HEADER_LOGO_BOX.height,
    borderRadius = 0,
    mode = "contain",
    exportScale = NUTRITION_PDF_HEADER_LOGO_EXPORT_SCALE,
    preserveSourceSize = false,
    trimTransparentPadding = true,
    trimUniformEdgeMatte = true,
  } = {},
) {
  const normalizedImageDataUrl = normalizeText(imageDataUrl);
  if (!normalizedImageDataUrl) {
    return null;
  }

  return await convertImageUrlToPngDataUrlImpl(normalizedImageDataUrl, {
    targetWidth: preserveSourceSize ? 0 : width,
    targetHeight: preserveSourceSize ? 0 : height,
    borderRadius,
    mode,
    exportScale: preserveSourceSize ? 1 : exportScale,
    renderWidthForBorderRadius: preserveSourceSize ? width : 0,
    renderHeightForBorderRadius: preserveSourceSize ? height : 0,
    trimTransparentPadding,
    trimUniformEdgeMatte,
  });
}

export const __testables = {
  buildNutritionPlanPdfSnapshotsFromSources,
  convertImageUrlToPngDataUrl,
  detectUniformEdgeMatte,
  resolveVisibleImageContentBounds,
};

function resolveStaticAssetUrl(assetModule, { imageModule = null } = {}) {
  const resolvedImageModule = imageModule ?? getReactNativeImageModule();
  if (!assetModule) {
    return null;
  }

  if (typeof assetModule === "string") {
    return normalizeText(assetModule) || null;
  }

  return normalizeText(resolvedImageModule?.resolveAssetSource?.(assetModule)?.uri) || null;
}

function getReactNativeImageModule() {
  try {
    return require("react-native")?.Image ?? null;
  } catch {
    return null;
  }
}

async function loadExpoAssetModule() {
  try {
    return await import("expo-asset");
  } catch {
    return null;
  }
}

async function resolveExpoAssetUrl(
  assetModule,
  { loadExpoAssetModuleImpl = loadExpoAssetModule } = {},
) {
  if (!assetModule) {
    return null;
  }

  try {
    const expoAssetModule = await loadExpoAssetModuleImpl();
    const Asset = expoAssetModule?.Asset ?? expoAssetModule?.default?.Asset ?? null;
    if (!Asset?.loadAsync) {
      return null;
    }

    const resolvedAssets = await Asset.loadAsync(assetModule);
    const resolvedAsset = Array.isArray(resolvedAssets)
      ? resolvedAssets[0]
      : resolvedAssets;

    return (
      normalizeText(resolvedAsset?.localUri) ||
      normalizeText(resolvedAsset?.uri) ||
      null
    );
  } catch {
    return null;
  }
}

async function resolveStaticPdfFontAsset(
  assetModule,
  {
    loadExpoAssetModuleImpl = loadExpoAssetModule,
    imageModule = null,
    fetchImpl = typeof fetch === "function" ? fetch : null,
  } = {},
) {
  const assetUrl =
    (await resolveExpoAssetUrl(assetModule, {
      loadExpoAssetModuleImpl,
    })) ?? resolveStaticAssetUrl(assetModule, { imageModule });

  if (!assetUrl || typeof fetchImpl !== "function") {
    return null;
  }

  try {
    const response = await fetchImpl(assetUrl);
    if (!response.ok) {
      return null;
    }

    const fontArrayBuffer = await response.arrayBuffer();
    const fontBase64 = arrayBufferToBase64(fontArrayBuffer);
    if (!fontBase64) {
      return null;
    }

    return {
      fileName: NUTRITION_PDF_ICON_FONT_FILE_NAME,
      data: fontBase64,
      fontFamily: NUTRITION_PDF_ICON_FONT_FAMILY,
    };
  } catch {
    return null;
  }
}

export async function resolveNutritionPlanPdfImageSource(
  imageUrl,
  {
    fetchImpl = typeof fetch === "function" ? fetch : null,
    blobToDataUrlImpl = blobToDataUrl,
    convertBlobToPngDataUrlImpl = convertBlobToPngDataUrl,
    convertImageUrlToPngDataUrlImpl = convertImageUrlToPngDataUrl,
  } = {},
) {
  const normalizedUrl = normalizeText(imageUrl);
  if (!normalizedUrl) {
    return null;
  }

  if (normalizedUrl.startsWith("data:")) {
    const dataUrlMimeType = parseDataUrlMimeType(normalizedUrl);
    if (!isImageMimeType(dataUrlMimeType)) {
      return null;
    }

    if (isPdfSafeImageMimeType(dataUrlMimeType)) {
      return normalizedUrl;
    }

    return await convertImageUrlToPngDataUrlImpl(normalizedUrl);
  }

  if (typeof fetchImpl !== "function") {
    return null;
  }

  try {
    const response = await fetchImpl(normalizedUrl);
    if (!response.ok) {
      return null;
    }

    const imageBlob = await response.blob();
    const resolvedMimeType = resolveFetchedImageMimeType(
      response,
      imageBlob,
      normalizedUrl,
    );

    if (!isImageMimeType(resolvedMimeType)) {
      return null;
    }

    if (isPdfSafeImageMimeType(resolvedMimeType)) {
      if (isPdfSafeImageMimeType(imageBlob?.type)) {
        return await blobToDataUrlImpl(imageBlob);
      }

      const imageArrayBuffer = await imageBlob.arrayBuffer();
      return arrayBufferToDataUrl(imageArrayBuffer, resolvedMimeType);
    }

    if (isImageMimeType(imageBlob?.type)) {
      return await convertBlobToPngDataUrlImpl(imageBlob);
    }

    const imageArrayBuffer = await imageBlob.arrayBuffer();
    if (!imageArrayBuffer) {
      return null;
    }

    if (resolvedMimeType === "image/webp" || resolvedMimeType === "image/svg+xml") {
      const normalizedImageDataUrl = arrayBufferToDataUrl(
        imageArrayBuffer,
        resolvedMimeType,
      );
      if (!normalizedImageDataUrl) {
        return null;
      }

      return await convertImageUrlToPngDataUrlImpl(normalizedImageDataUrl);
    }

    if (isPdfSafeImageMimeType(resolvedMimeType)) {
      return arrayBufferToDataUrl(imageArrayBuffer, resolvedMimeType);
    }

    if (isImageMimeType(imageBlob?.type)) {
      return await blobToDataUrlImpl(imageBlob);
    }

    return null;
  } catch {
    return null;
  }
}

export async function resolveStaticNutritionPlanPdfImageAsset(
  assetModule,
  {
    imageModule = null,
    loadExpoAssetModuleImpl = loadExpoAssetModule,
    convertImageUrlToPngDataUrlImpl = convertImageUrlToPngDataUrl,
    resolveNutritionPlanPdfImageSourceImpl = resolveNutritionPlanPdfImageSource,
  } = {},
) {
  const assetUrl =
    (await resolveExpoAssetUrl(assetModule, {
      loadExpoAssetModuleImpl,
    })) ?? resolveStaticAssetUrl(assetModule, { imageModule });
  if (!assetUrl) {
    return null;
  }

  if (assetUrl.startsWith("data:")) {
    const dataUrlMimeType = parseDataUrlMimeType(assetUrl);
    if (isPdfSafeImageMimeType(dataUrlMimeType)) {
      return assetUrl;
    }
  }

  const rasterizedAssetDataUrl = await convertImageUrlToPngDataUrlImpl(assetUrl);
  if (rasterizedAssetDataUrl) {
    return rasterizedAssetDataUrl;
  }

  return await resolveNutritionPlanPdfImageSourceImpl(assetUrl);
}

async function safeResolveAsset(promiseFactory) {
  try {
    return await promiseFactory();
  } catch {
    return null;
  }
}

async function resolvePdfAssets(snapshot) {
  const [coachLogoDataUrl, efortLogoDataUrl] = await Promise.all([
    safeResolveAsset(() =>
      resolveNutritionPlanPdfImageSource(snapshot?.branding?.coachLogoUrl),
    ),
    safeResolveAsset(() => resolveStaticNutritionPlanPdfImageAsset(BlackPng)),
  ]);

  const [formattedCoachLogoDataUrl, formattedEfortLogoDataUrl] = await Promise.all([
    safeResolveAsset(() =>
      formatNutritionPlanPdfHeaderLogoDataUrl(coachLogoDataUrl, {
        borderRadius: 8,
        trimTransparentPadding: false,
        trimUniformEdgeMatte: false,
      }),
    ),
    safeResolveAsset(() =>
      formatNutritionPlanPdfHeaderLogoDataUrl(efortLogoDataUrl),
    ),
  ]);

  return {
    coachLogoImage: formattedCoachLogoDataUrl ?? coachLogoDataUrl,
    efortLogoImage: formattedEfortLogoDataUrl ?? efortLogoDataUrl,
  };
}

async function getPdfMake() {
  if (!pdfMakeLoaderPromise) {
    pdfMakeLoaderPromise = Promise.all([
      import("pdfmake/build/pdfmake"),
      import("pdfmake/build/vfs_fonts"),
      resolveStaticPdfFontAsset(
        require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Ionicons.ttf"),
      ),
    ]).then(([pdfMakeModule, pdfFontsModule, iconFontAsset]) => {
      const pdfMake = pdfMakeModule.default ?? pdfMakeModule;
      const virtualFileSystem = pdfFontsModule.default ?? pdfFontsModule;

      if (!pdfMake.__efortVfsRegistered) {
        pdfMake.addVirtualFileSystem(virtualFileSystem);
        pdfMake.__efortVfsRegistered = true;
      }

      if (iconFontAsset && !pdfMake.__efortPdfIconFontRegistered) {
        pdfMake.addVirtualFileSystem({
          [iconFontAsset.fileName]: {
            data: iconFontAsset.data,
            encoding: "base64",
          },
        });
        pdfMake.addFonts({
          [iconFontAsset.fontFamily]: {
            normal: iconFontAsset.fileName,
            bold: iconFontAsset.fileName,
            italics: iconFontAsset.fileName,
            bolditalics: iconFontAsset.fileName,
          },
        });
        pdfMake.__efortPdfIconFontRegistered = true;
      }

      return {
        pdfMake,
        iconFontFamily: iconFontAsset?.fontFamily || null,
      };
    });
  }

  return pdfMakeLoaderPromise;
}

export async function downloadNutritionPlanPdf({
  planEntity = null,
  draft = null,
  rootRecord = null,
  coach = null,
  athleteName = "",
  language = "en",
} = {}) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof URL === "undefined"
  ) {
    throw new Error("Nutrition plan PDF export is only available on web");
  }

  const exportedAt = new Date();
  const snapshot = buildNutritionPlanPdfSnapshot({
    planEntity,
    draft,
    rootRecord,
    coach,
    athleteName,
    language,
    exportedAt,
  });

  if (!snapshot) {
    throw new Error("Missing nutrition plan snapshot");
  }

  const [{ pdfMake, iconFontFamily }, assets] = await Promise.all([
    getPdfMake(),
    resolvePdfAssets(snapshot),
  ]);

  const documentDefinition = buildNutritionPlanPdfDefinition(snapshot, {
    ...assets,
    iconFontFamily,
  });
  await pdfMake.createPdf(documentDefinition).download(snapshot.fileName);

  return snapshot.fileName;
}

export async function downloadNutritionPlansPdf({
  planSources = [],
  coach = null,
  athleteName = "",
  language = "en",
} = {}) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof URL === "undefined"
  ) {
    throw new Error("Nutrition plan PDF export is only available on web");
  }

  const exportedAt = new Date();
  const { snapshots, skippedPlanNames } = buildNutritionPlanPdfSnapshotsFromSources({
    planSources,
    coach,
    athleteName,
    language,
    exportedAt,
  });

  if (snapshots.length === 0) {
    const error = new Error("Missing nutrition plan snapshots");
    error.skippedPlanNames = skippedPlanNames;
    throw error;
  }

  const [{ pdfMake, iconFontFamily }, assets] = await Promise.all([
    getPdfMake(),
    resolvePdfAssets(snapshots[0]),
  ]);
  const fileName = buildNutritionPlansPdfFileName({
    athleteName,
    language,
    exportedAt,
  });
  const documentDefinition = buildNutritionPlansPdfDefinition(snapshots, {
    ...assets,
    iconFontFamily,
  });

  await pdfMake.createPdf(documentDefinition).download(fileName);

  return {
    fileName,
    skippedPlanNames,
  };
}
